import { errorLogger } from "@Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IUser } from "@/Common/interfaces/user";
import { GraphqlQueryResponse } from "../types/GraphqlQueryResponse";
import { IPaginationInput } from "@Common/types/common-queries";

export async function getUsersSelect(locationId: string, position: string[], status: string) {
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				fullName
				employeeId
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			locationId,
			position,
			status,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getUsers;
	} catch (err) {
		errorLogger(err as Error, { function: "getUsersSelect", message: "Error getting users select", variables });
		return null;
	}
}

export async function getAccessSettingsUsers(locationId: string, pagination: IPaginationInput) {
	const query = gql`
		query getAccessSettingsUsers($locationId: String!, $pagination: PaginationInput) {
			getAccessSettingsUsers(locationId: $locationId, pagination: $pagination) {
				items {
					_id
					fullName
					position
				}
				pageInfo {
					hasNextPage
					endCursor
				}
			}
		}
	`;

	const variables = {
		locationId,
		pagination,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data?.data?.getAccessSettingsUsers;
	} catch (err) {
		errorLogger(err as Error, { function: "getAccessSettingsUsers", message: "Error getting access settings users", variables });
		return null;
	}
}

const PROPERTIES = [
	"_id",
	"username",
	"locationId",
	"position",
	"payrollPosition",
	"fullName",
	"employeeId",
	"supervisor",
	"communications",
	"preferences",
	"currentStatus",
] as const;

export interface IGetUsersResponse extends Pick<IUser, (typeof PROPERTIES)[number]> {}

export async function getUsers(locationId: string, position: string | null = null) {
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				username
				locationId
				position
				payrollPosition
				fullName
				employeeId
				supervisor {
					_id
					fullName
				}
				communications {
					acceptsTxt
					communicationType
					usage
					value
				}
				preferences {
					landingLocation
					landingPage
				}
				currentStatus {
					status
				}
				kmsUserId
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			locationId,
			position,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getUsers", IGetUsersResponse[]> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getUsers;
	} catch (err) {
		errorLogger(err as Error, { function: "getUsers", message: "Error getting users", variables });
		return null;
	}
}

export async function globalSignOut(locationId: string, username: string) {
	const query = gql`
		query globalSignOut($locationId: String!, $username: String!) {
			globalSignOut(locationId: $locationId, username: $username)
		}
	`;

	const variables = {
		locationId,
		username,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.globalSignOut;
	} catch (err) {
		errorLogger(err as Error, { function: "globalSignOut", message: "Error signing out user", variables });
		return null;
	}
}

export async function getUserActions(userId: string, locationId: string) {
	const query = gql`
		query getUser($_id: String!, $locationId: String!) {
			getUser(_id: $_id, locationId: $locationId) {
				_id
				actions
			}
		}
	`;

	const variables = {
		_id: userId,
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getUser;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function refetchLoggedUser(userId: string, locationId: string) {
	const query = gql`
		query getUser($_id: String!, $locationId: String!) {
			getUser(_id: $_id, locationId: $locationId) {
				_id
				username
				fullName
				position
				locationId
				employeeId
				supervisor {
					_id
				}
				communications {
					acceptsTxt
					communicationType
					usage
					value
				}
				preferences {
					landingPage
					landingLocation
				}
				profilePicture
				actions
			}
		}
	`;

	const variables = {
		locationId,
		_id: userId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getUser;
	} catch (err) {
		errorLogger(err as Error, { function: "refetchLoggedUser", message: "Error refetching user" });
		return null;
	}
}

export async function getAuthorizationUsers(locationId: string) {
	const query = gql`
		query getUsers($locationId: String!) {
			getUsers(locationId: $locationId) {
				_id
				fullName
				position
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getUsers;
	} catch (err) {
		errorLogger(err as Error, { function: "getAuthorizationUsers", message: "Error getting users" });
		return null;
	}
}
